import { useCallback } from 'react';
import { toast } from 'sonner';
import { useCreateGeneratedDocument } from '@/feature/downloads/hooks/downloadsAPI';
import type {
  DocumentEntry,
  DocumentKey,
  DocumentViewerPatient,
  MedicationDataType,
} from './types';
import { mapDocumentKeyToApiInput } from './mapDocumentKeyToApiInput';

interface UseDocumentDownloadArgs {
  documentKey?: DocumentKey;
  entryKind?: DocumentEntry['kind'];
  sourceRequestId?: string;
  medicationDataType?: MedicationDataType;
  patientId?: string;
  patient?: DocumentViewerPatient;
}

export interface UseDocumentDownloadResult {
  disabled: boolean;
  isGenerating: boolean;
  download: () => void;
}

export const useDocumentDownload = ({
  documentKey,
  entryKind,
  sourceRequestId,
  medicationDataType,
  patientId,
}: UseDocumentDownloadArgs): UseDocumentDownloadResult => {
  const { mutate, isPending } = useCreateGeneratedDocument();

  const download = useCallback(() => {
    if (!documentKey || !patientId) return;

    const input = mapDocumentKeyToApiInput(documentKey, patientId, {
      entryKind,
      sourceRequestId,
      medicationDataType,
    });
    if (!input) {
      toast.error('Unable to download this document.', {
        description: 'This record type is not supported for download yet.',
      });
      return;
    }

    mutate(input);
  }, [
    documentKey,
    entryKind,
    sourceRequestId,
    medicationDataType,
    patientId,
    mutate,
  ]);

  return {
    disabled: !documentKey || !patientId || isPending,
    isGenerating: isPending,
    download,
  };
};
